/**
 * useInstrumentSearch Hook
 * Manages instrument search functionality
 * 
 * ⚠️ IMPORTANT: Per ADR-FE-001, this hook only orchestrates search.
 * All search logic is handled by backend Instrument Service.
 */

import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { searchInstruments, clearSearchResults } from '../../../store/slices/marketDataSlice';
import { Instrument } from '../../../types/instrument';

export interface UseInstrumentSearchResult {
  /**
   * Search query
   */
  query: string;
  /**
   * Search results
   */
  results: Instrument[];
  /**
   * Loading state
   */
  loading: boolean;
  /**
   * Error message
   */
  error: string | null;
  /**
   * Perform search
   */
  search: (query: string, exchange?: string, segment?: string, limit?: number) => Promise<void>;
  /**
   * Clear search results
   */
  clear: () => void;
}

export function useInstrumentSearch(): UseInstrumentSearchResult {
  const dispatch = useAppDispatch();
  const searchResults = useAppSelector((state) => state.marketData.searchResults);
  const searchQuery = useAppSelector((state) => state.marketData.searchQuery);
  const searchLoading = useAppSelector((state) => state.marketData.searchLoading);
  const error = useAppSelector((state) => state.marketData.error);

  const search = useCallback(
    async (query: string, exchange?: string, segment?: string, limit?: number) => {
      if (!query.trim()) {
        dispatch(clearSearchResults());
        return;
      }

      try {
        await dispatch(
          searchInstruments({
            query: query.trim(),
            exchange,
            segment,
            limit: limit || 20,
          })
        ).unwrap();
      } catch (err) {
        // Error handled by Redux slice
        // eslint-disable-next-line no-console
        console.error('Failed to search instruments:', err);
      }
    },
    [dispatch]
  );

  const clear = useCallback(() => {
    dispatch(clearSearchResults());
  }, [dispatch]);

  return {
    query: searchQuery,
    results: searchResults,
    loading: searchLoading,
    error,
    search,
    clear,
  };
}

